import {Component, computed, effect, inject, signal, ChangeDetectionStrategy} from '@angular/core';
import { DatePipe } from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Api} from '../../../../api/api';
import {getEnergySummaries} from '../../../../api/functions';
import {rxResource, toSignal} from '@angular/core/rxjs-interop';
import {from, interval, of} from 'rxjs';
import {catchError, finalize} from 'rxjs/operators';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatDatepicker, MatDatepickerModule} from '@angular/material/datepicker';
import {MatButtonToggle, MatButtonToggleGroup} from '@angular/material/button-toggle';
import {MatInputModule} from '@angular/material/input';
import { BatteryType, EnergyCombinationType, EnergyType, Interval } from './base-chart.component';
import {Router, RouterLink} from '@angular/router';
import {PerformanceChartComponent} from './performance-chart.component';
import {BatteryPowerDayChartComponent} from './battery-power-day-chart.component';
import {BatteryPowerChartComponent} from './battery-power-chart.component';
import {DayDatepickerComponent} from '../datepicker/day-datepicker.component';
import {MonthDatepickerComponent} from '../datepicker/month-datepicker.component';
import {YearDatepickerComponent} from '../datepicker/year-datepicker.component';
import {BatteryChargeMonthlyChartComponent} from './battery-charge-monthly-chart.component';
import {MatIconModule} from '@angular/material/icon';
import {GridBarChartComponent} from './grid-bar-chart.component';
import {GridDayChartComponent} from './grid-day-chart.component';
import {LocationStateService} from '../../../../common/services/location-state.service';

import {environment} from '../../../../environments/environment';
import {SAMPLE} from './sample-data/energy-chart-daily-data';
import {SAMPLE_MONTH} from './sample-data/energy-chart-data-month';
import {SAMPLE_YEAR} from './sample-data/energy-chart-data-year';
import {GridOriginChartComponent} from './grid-oirgin-chart.component';
import {BatteryOriginChartComponent} from './battery-oirgin-chart.component';
import {UsageChartComponent} from './usage-chart.component';
import {ConsumptionOirginChartComponent} from './consumption-oirgin-chart.component';

@Component({
  selector: 'app-energy-chart',
  imports: [
    FormsModule,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    PerformanceChartComponent,
    BatteryPowerDayChartComponent,
    BatteryPowerChartComponent,
    DayDatepickerComponent,
    MonthDatepickerComponent,
    YearDatepickerComponent,
    BatteryChargeMonthlyChartComponent,
    MatIconModule,
    GridBarChartComponent,
    GridDayChartComponent,
    RouterLink,
    GridOriginChartComponent,
    BatteryOriginChartComponent,
    ConsumptionOirginChartComponent,
    UsageChartComponent
],
  providers: [
    DatePipe,
    //provideMomentDateAdapter(MONTH_YEAR_FORMAT)
  ],
  templateUrl: './energy-chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './energy-chart.component.css',
})
export class EnergyChartComponent {
  private router = inject(Router);

  api = inject(Api);
  datePipe = inject(DatePipe);
  isLoading = signal<boolean>(false);
  locationStateService = inject(LocationStateService);

  selectedEnergyOption = signal<EnergyType>('production');
  selectedBatteryOption = signal<BatteryType>('charge');
  selectedChartOption = signal<EnergyCombinationType>('performance');
  selectedInterval = signal<Interval>('day');
  selectedDate = signal(new Date());

  params = computed(() => ({
    location_id: this.locationStateService.currentLocation()?.id ?? '',
    aggregation: this.selectedInterval() === 'all' ? 'total' : this.selectedInterval(),
    at: this.selectedInterval() === 'all' ? undefined : this.selectedDate().toISOString(),
  }));

  resource = rxResource({
    params: this.params,
    defaultValue: environment.showDefault ? SAMPLE : undefined,
    stream: (loaderParams) => {
      this.isLoading.set(true);
      switch (this.selectedInterval()) {
        case 'day':
          return from(this.api.invoke(getEnergySummaries, loaderParams.params)).pipe(
            catchError((error) => {
              if (environment.showDefault) {
                console.error('Service is not reachable. Using daily sample data.', error);
                return of(SAMPLE);
              }
              throw error;
            }),
            finalize(() => this.isLoading.set(false)),
          );
        case 'month':
          return from(this.api.invoke(getEnergySummaries, loaderParams.params)).pipe(
            catchError((error) => {
              if (environment.showDefault) {
                console.error('Service is not reachable. Using monthly sample data.', error);
                return of(SAMPLE_MONTH);
              }
              throw error;
            }),
            finalize(() => this.isLoading.set(false)),
          );
        case 'year':
          return from(this.api.invoke(getEnergySummaries, loaderParams.params)).pipe(
            catchError((error) => {
              if (environment.showDefault) {
                console.error('Service is not reachable. Using yearly sample data.', error);
                return of(SAMPLE_YEAR);
              }
              throw error;
            }),
            finalize(() => this.isLoading.set(false)),
          );
        case 'all':
        default:
          return from(this.api.invoke(getEnergySummaries, loaderParams.params)).pipe(
            catchError((error) => {
              if (environment.showDefault) {
                console.error('Service is not reachable. Using daily sample data.', error);
                return of(SAMPLE);
              }
              throw error;
            }),
            finalize(() => this.isLoading.set(false)),
          );
      }
    },
  });

  poll = toSignal(interval(5000));

  onEnergySelectionChange(event: any) {
    this.selectedEnergyOption.set(event.value);

    //assign correct url depending on energy-option mode,
    // so that user stays at the same page if he makes a reload
    const currentUrl = window.location.href.split('#');
    window.location.assign(currentUrl[0] + '#' + event.value);
    // always load performance charts if user switches EnergyOption (usage only makes sense with production)
    if (this.selectedChartOption() === 'origin' && event.value !== 'production') {
      this.selectedChartOption = signal<'performance'>('performance');
      this.resource.reload();
    }
    if (this.selectedEnergyOption() === 'battery') {
      this.selectedBatteryOption.set('charge');
    }
  }

  onBatterySelectionChange(event: any) {
    this.selectedBatteryOption.set(event.value);
    this.triggerReload();
  }

  onChartSelectionChange(event: any) {
    this.selectedChartOption.set(event.value);
    if (this.selectedChartOption() === 'origin') {
      this.selectedInterval.set('day');
      this.triggerReload();
    }
  }

  onIntervalSelectionChange(event: any) {
    this.selectedInterval.set(event.value);
    this.triggerReload();
  }

  onMonthSelected(month: Date, datepicker: MatDatepicker<Date>) {
    this.selectedDate.set(month);
    this.triggerReload();
    datepicker.close();
  }

  onYearSelected(year: Date, datepicker: MatDatepicker<Date>) {
    this.selectedDate.set(new Date(year.getFullYear(), 0)); // Set to Jan of selected year
    this.triggerReload();
    datepicker.close();
  }

  get selectedMonth(): string {
    return this.datePipe.transform(this.selectedDate(), 'MMMM yyyy', undefined, 'de') || '';
  }

  get selectedYear(): string {
    return this.datePipe.transform(this.selectedDate(), 'yyyy', undefined, 'de') || '';
  }

  updateDate(event: any) {
    const newDate = event?.value || event;
    if (newDate) {
      // Set loading state immediately when date changes, before updating the date
      // This ensures the overlay appears as soon as the user changes the date
      this.isLoading.set(true);
      this.selectedDate.set(newDate);
      // The rxResource will automatically reload when params changes,
      // but we also explicitly trigger reload to ensure loading state is managed
      this.resource.reload();
    }
  }

  incrementDate() {
    this.selectedDate.update((d) => {
      const newDate = new Date(d);
      switch (this.selectedInterval()) {
        case 'day':
          newDate.setDate(newDate.getDate() + 1);
          break;
        case 'month':
          newDate.setMonth(newDate.getMonth() + 1);
          break;
        case 'year':
          newDate.setFullYear(newDate.getFullYear() + 1);
          break;
      }
      return newDate;
    });
    this.triggerReload();
  }

  decrementDate() {
    this.selectedDate.update((d) => {
      const newDate = new Date(d);
      switch (this.selectedInterval()) {
        case 'day':
          newDate.setDate(newDate.getDate() - 1);
          break;
        case 'month':
          newDate.setMonth(newDate.getMonth() - 1);
          break;
        case 'year':
          newDate.setFullYear(newDate.getFullYear() - 1);
          break;
      }
      return newDate;
    });
    this.triggerReload();
  }

  onResourceChange() {
    console.log('Resource change detected');
  }

  constructor() {
    this.setCorrectEnergyMode();
    // The loading state is managed by the loader's finalize operator
    // This effect is kept as a fallback for edge cases, but it should not
    // interfere with the normal loading flow managed by the loader
  }

  private triggerReload() {
    this.isLoading.set(true);
    this.resource.reload();
  }

  setCorrectEnergyMode() {
    const currentUrl = this.router.url; // e.g. /dashboard
    switch (true) {
      case currentUrl.includes('production'):
        this.selectedEnergyOption.set('production');
        break;

      case currentUrl.includes('battery'):
        this.selectedEnergyOption.set('battery');
        break;
      case currentUrl.includes('consumption'):
        this.selectedEnergyOption.set('consumption');
        break;
      case currentUrl.includes('grid'):
        this.selectedEnergyOption.set('grid');
        break;

      default:
        this.selectedEnergyOption.set('production');
    }
  }
}
